'use client'

import { useState, useEffect } from 'react'
import { useCurrency } from '@/lib/currency'
import { adminPayoutApi } from '@/lib/api'
import { getApiUrl } from '@/lib/apiUrl'
import type { PayoutRequest } from '@/types'
import { CreditCard, Building2, Clock, CheckCircle, XCircle, AlertCircle, Eye } from 'lucide-react'

interface PayoutManagementProps {
  users?: any[]
  onUpdate?: () => void
}

export default function PayoutManagement({ users = [], onUpdate }: PayoutManagementProps) {
  const { format } = useCurrency()
  const [loading, setLoading] = useState(true)
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([])
  const [selectedRequest, setSelectedRequest] = useState<PayoutRequest | null>(null)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'failed'>('all')
  const [processingAction, setProcessingAction] = useState<string | null>(null)
  const [approvalNotes, setApprovalNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [transferId, setTransferId] = useState('')
  const [landlordPayoutAccounts, setLandlordPayoutAccounts] = useState<any[]>([])
  const [loadingAccounts, setLoadingAccounts] = useState(true)

  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null

  // Load all landlord payout (bank) accounts for admin to see when processing payments
  const loadLandlordPayoutAccounts = async () => {
    if (!token) return
    setLoadingAccounts(true)
    try {
      const res = await fetch(`${getApiUrl()}/admin/landlords/payout-accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await res.json()
      if (res.ok && data.landlords) {
        setLandlordPayoutAccounts(data.landlords)
      }
    } catch (e) {
      console.error('Error loading landlord payout accounts:', e)
    } finally {
      setLoadingAccounts(false)
    }
  }

  // Load payout requests
  const loadPayoutRequests = async () => {
    if (!token) return
    
    try {
      const params: any = {}
      if (filter !== 'all') {
        params.status = filter
      }
      
      const response = await adminPayoutApi.getHistory(token, params)
      if (response.success && response.data) {
        setPayoutRequests((response.data as any).payoutRequests || [])
      }
    } catch (error) {
      console.error('Error loading payout requests:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPayoutRequests()
  }, [filter])

  useEffect(() => {
    loadLandlordPayoutAccounts()
  }, [])

  // Approve payout request
  const handleApprove = async (requestId: string) => {
    if (!token) return
    
    setProcessingAction(`approve-${requestId}`)
    try {
      const response = await adminPayoutApi.approve(token, requestId, approvalNotes)
      if (response.success) {
        alert('Payout request approved successfully')
        setApprovalNotes('')
        setSelectedRequest(null)
        loadPayoutRequests()
        onUpdate?.()
      } else {
        alert(response.error || 'Failed to approve payout request')
      }
    } catch (error: any) {
      alert(error.message || 'Failed to approve payout request')
    } finally {
      setProcessingAction(null)
    }
  }

  // Reject payout request
  const handleReject = async (requestId: string) => {
    if (!token) return
    
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason')
      return
    }
    
    setProcessingAction(`reject-${requestId}`)
    try {
      const response = await adminPayoutApi.reject(token, requestId, rejectionReason, '')
      if (response.success) {
        alert('Payout request rejected successfully')
        setRejectionReason('')
        setSelectedRequest(null)
        loadPayoutRequests()
        onUpdate?.()
      } else {
        alert(response.error || 'Failed to reject payout request')
      }
    } catch (error: any) {
      alert(error.message || 'Failed to reject payout request')
    } finally {
      setProcessingAction(null)
    }
  }

  // Process payout (mark as processing/completed)
  const handleProcess = async (requestId: string) => {
    if (!token) return
    
    setProcessingAction(`process-${requestId}`)
    try {
      const response = await adminPayoutApi.process(token, requestId, transferId || undefined)
      if (response.success) {
        alert('Payout processed successfully')
        setTransferId('')
        setSelectedRequest(null)
        loadPayoutRequests()
        onUpdate?.()
      } else {
        alert(response.error || 'Failed to process payout')
      }
    } catch (error: any) {
      alert(error.message || 'Failed to process payout')
    } finally {
      setProcessingAction(null)
    }
  }

  const getStatusBadge = (status: PayoutRequest['status']) => {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
    
    switch (status) {
      case 'pending':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      case 'approved':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
          <CheckCircle className="w-3 h-3 mr-1" />
          Approved
        </span>
      case 'processing':
        return <span className={`${baseClasses} bg-purple-100 text-purple-800`}>
          <CreditCard className="w-3 h-3 mr-1" />
          Processing
        </span>
      case 'completed':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </span>
      case 'rejected':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <XCircle className="w-3 h-3 mr-1" />
          Rejected
        </span>
      case 'failed':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <AlertCircle className="w-3 h-3 mr-1" />
          Failed
        </span>
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Unknown</span>
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredRequests = filter === 'all' 
    ? payoutRequests 
    : payoutRequests.filter(req => req.status === filter)

  // Landlords with their saved payout (bank) account details – from dedicated API (full list) or fallback to users
  const landlordsForPayout = landlordPayoutAccounts.length > 0 ? landlordPayoutAccounts : (users || []).filter((u: any) => u.role === 'landlord')

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading payout requests...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payout Management</h2>
          <p className="text-gray-600 mt-1">Review and process landlord payout requests</p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-1">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">
            {payoutRequests.filter(r => r.status === 'pending').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-1">Approved</p>
          <p className="text-2xl font-bold text-blue-600">
            {payoutRequests.filter(r => r.status === 'approved').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-1">Processing</p>
          <p className="text-2xl font-bold text-purple-600">
            {payoutRequests.filter(r => r.status === 'processing').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-1">Completed</p>
          <p className="text-2xl font-bold text-green-600">
            {payoutRequests.filter(r => r.status === 'completed').length}
          </p>
        </div>
      </div>

      {/* Landlord payout accounts – bank details for processing rental payments after commission */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Landlord payout accounts</h3>
          <p className="text-sm text-gray-600 mt-0.5">Bank account details for all landlords (use when processing rental payouts after commission)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Landlord</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">KYC</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account number</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {landlordsForPayout.length > 0 ? (
                landlordsForPayout.map((landlord: any) => {
                  const pa = landlord.paymentAccount || {}
                  const hasAccount = !!(pa.bankName || pa.accountName || pa.accountNumber)
                  const verified = landlord.is_verified || landlord.isVerified || landlord.kyc?.status === 'verified'
                  const name = [landlord.firstName, landlord.lastName].filter(Boolean).join(' ') ||
                    [landlord.kyc_data?.firstName, landlord.kyc_data?.lastName].filter(Boolean).join(' ') || '—'
                  return (
                    <tr key={landlord.id || landlord._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{name || '—'}</p>
                          <p className="text-xs text-gray-500">{landlord.email || '—'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {verified ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3.5 h-3.5 mr-1 inline" />
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <Clock className="w-3.5 h-3.5 mr-1 inline" />
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{hasAccount ? (pa.bankName || '—') : <span className="text-gray-400">Not added</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{hasAccount ? (pa.accountName || '—') : <span className="text-gray-400">Not added</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">{hasAccount ? (pa.accountNumber || '—') : <span className="text-gray-400">Not added</span>}</td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    {loadingAccounts ? 'Loading landlord accounts...' : 'No landlord accounts to show.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout Requests Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Landlord</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRequests.length > 0 ? (
                filteredRequests.map((request) => {
                  const landlord = typeof request.landlord === 'object' ? request.landlord : null
                  const isProcessing = processingAction === `approve-${request.id}` || 
                                       processingAction === `reject-${request.id}` || 
                                       processingAction === `process-${request.id}`
                  
                  return (
                    <tr key={request.id || (request as any)._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {landlord ? `${landlord.firstName || ''} ${landlord.lastName || ''}`.trim() || landlord.email : 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">{landlord?.email || 'N/A'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {format(request.amount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          {request.paymentMethod === 'bank_transfer' ? (
                            <>
                              <Building2 className="w-4 h-4" />
                              <span>Bank Transfer</span>
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4" />
                              <span>Stripe Connect</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(request.requestedAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <button
                          onClick={() => setSelectedRequest(selectedRequest?.id === request.id ? null : request)}
                          className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                          disabled={isProcessing}
                        >
                          <Eye className="w-4 h-4" />
                          {selectedRequest?.id === request.id ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No payout requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Payout Request Details</h3>
              <button
                onClick={() => {
                  setSelectedRequest(null)
                  setApprovalNotes('')
                  setRejectionReason('')
                  setTransferId('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Request Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Request ID</p>
                  <p className="font-medium text-gray-900">{selectedRequest.id || (selectedRequest as any)._id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Amount</p>
                  <p className="font-medium text-gray-900">{format(selectedRequest.amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Payment Method</p>
                  <p className="font-medium text-gray-900">
                    {selectedRequest.paymentMethod === 'bank_transfer' ? 'Bank Transfer' : 'Stripe Connect'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Requested At</p>
                  <p className="font-medium text-gray-900">{formatDate(selectedRequest.requestedAt)}</p>
                </div>
                {selectedRequest.completedAt && (
                  <div>
                    <p className="text-sm text-gray-600">Completed At</p>
                    <p className="font-medium text-gray-900">{formatDate(selectedRequest.completedAt)}</p>
                  </div>
                )}
              </div>

              {/* Bank Details */}
              {selectedRequest.bankDetails && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Bank Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedRequest.bankDetails.bankName && (
                      <div>
                        <p className="text-sm text-gray-600">Bank Name</p>
                        <p className="font-medium text-gray-900">{selectedRequest.bankDetails.bankName}</p>
                      </div>
                    )}
                    {selectedRequest.bankDetails.accountName && (
                      <div>
                        <p className="text-sm text-gray-600">Account Name</p>
                        <p className="font-medium text-gray-900">{selectedRequest.bankDetails.accountName}</p>
                      </div>
                    )}
                    {selectedRequest.bankDetails.accountNumber && (
                      <div>
                        <p className="text-sm text-gray-600">Account Number</p>
                        <p className="font-medium text-gray-900">{selectedRequest.bankDetails.accountNumber}</p>
                      </div>
                    )}
                    {selectedRequest.bankDetails.swiftCode && (
                      <div>
                        <p className="text-sm text-gray-600">SWIFT Code</p>
                        <p className="font-medium text-gray-900">{selectedRequest.bankDetails.swiftCode}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rejection/Failure Reason */}
              {(selectedRequest.rejectionReason || selectedRequest.failureReason) && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-medium text-red-900 mb-2">
                    {selectedRequest.rejectionReason ? 'Rejection Reason' : 'Failure Reason'}
                  </h4>
                  <p className="text-red-700">{selectedRequest.rejectionReason || selectedRequest.failureReason}</p>
                </div>
              )}

              {/* Actions */}
              {selectedRequest.status === 'pending' && (
                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Approval Notes (Optional)</label>
                    <textarea
                      value={approvalNotes}
                      onChange={(e) => setApprovalNotes(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="Add any notes for this approval..."
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApprove(selectedRequest.id || (selectedRequest as any)._id)}
                      disabled={processingAction === `approve-${selectedRequest.id}`}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300"
                    >
                      {processingAction === `approve-${selectedRequest.id}` ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Please provide a rejection reason:')
                        if (reason) {
                          setRejectionReason(reason)
                          setTimeout(() => handleReject(selectedRequest.id || (selectedRequest as any)._id), 100)
                        }
                      }}
                      disabled={processingAction === `reject-${selectedRequest.id}`}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-300"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}

              {selectedRequest.status === 'approved' && (
                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Transfer ID (Optional)</label>
                    <input
                      type="text"
                      value={transferId}
                      onChange={(e) => setTransferId(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Bank transfer reference or Stripe transfer ID"
                    />
                  </div>
                  <button
                    onClick={() => handleProcess(selectedRequest.id || (selectedRequest as any)._id)}
                    disabled={processingAction === `process-${selectedRequest.id}`}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300"
                  >
                    {processingAction === `process-${selectedRequest.id}` ? 'Processing...' : 'Mark as Processing'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

